/**
 * Module-level references to Auth0 SDK functions.
 *
 * The axios interceptor cannot use React hooks, so we store references here
 * that the Authenticated component populates as soon as Auth0 is ready.
 * This lets the interceptor call getAccessTokenSilently() / getIdTokenClaims()
 * directly instead of manually parsing Auth0's internal localStorage cache.
 */

type GetIdTokenClaims = () => Promise<any>;
type GetAccessTokenSilently = (options?: any) => Promise<string>;

let _getIdTokenClaims: GetIdTokenClaims | null = null;
let _getAccessTokenSilently: GetAccessTokenSilently | null = null;
let _isAuthenticated = false;

/** Called from the Authenticated component on every render so refs stay current. */
export const setAuth0Client = (
  getIdTokenClaims: GetIdTokenClaims,
  getAccessTokenSilently: GetAccessTokenSilently,
  isAuthenticated: boolean,
): void => {
  _getIdTokenClaims = getIdTokenClaims;
  _getAccessTokenSilently = getAccessTokenSilently;
  _isAuthenticated = isAuthenticated;
};

/**
 * Use Auth0 SDK to force a fresh token refresh and return the new idToken.
 *
 * This is the correct way to refresh tokens because:
 * - Auth0 SDK handles refresh token rotation automatically
 * - It always returns a new id_token (which the Sonio API uses for auth)
 * - It avoids parsing Auth0's internal localStorage cache structure
 *
 * Returns null if SDK isn't ready or refresh fails, so callers can fall back.
 */
export const silentlyRefreshTokens = async (): Promise<string | null> => {
  if (!_isAuthenticated || !_getAccessTokenSilently || !_getIdTokenClaims) {
    return null;
  }
  try {
    // cacheMode: 'off' forces a real token refresh even if the cache looks valid.
    // We use this specifically when a 401 was received, meaning the cached token
    // is already rejected by the server.
    await _getAccessTokenSilently({ cacheMode: 'off' });
    const claims = await _getIdTokenClaims();
    const newIdToken = claims?.__raw;
    if (newIdToken) {
      localStorage.setItem('idToken', newIdToken);
    }
    return newIdToken ?? null;
  } catch {
    return null;
  }
};
